/**
 * Sales — document model and configuration.
 *
 * The sales cycle runs Quotation → Sales Order → Delivery → Invoice → Receipt,
 * with Credit Notes against posted invoices. Which of those stages are actually
 * required is configuration, not code: a consulting firm invoices directly, a
 * manufacturer runs the full chain, and both are legitimate. The flow engine in
 * flow.ts reads this config to decide what a document can convert into and what
 * must exist before an invoice can be raised.
 */

export type SalesDocType =
  | "quotation"
  | "sales-order"
  | "delivery-note"
  | "invoice"
  | "credit-note"
  | "debit-note"
  | "receipt"
  | "proforma";

/** How a line is treated for GST. Drives which tax heads apply. */
export type TaxTreatment =
  | "taxable"
  | "zero-rated"        // exports and SEZ supplies with LUT
  | "exempt"            // section 11 exemption
  | "nil-rated"
  | "non-gst"           // outside GST — alcohol, petroleum
  | "reverse-charge";   // recipient pays

export type SupplyType = "intra-state" | "inter-state" | "export";

export interface SalesLine {
 priceDiscount?:import('../document-adjustments').Discount;lineKind?:'item'|'charge';
  taxInclusive?:boolean;
  id: string;
  itemId?: string;
  description: string;
  /** HSN for goods, SAC for services. Mandatory above the turnover threshold. */
  hsnSac?: string;
  /** Unit Quantity Code as used in GSTR-1, e.g. NOS, KGS, MTR. */
  uqc?: string;
  quantity: number;
  unitPrice: number;
  discountPercent?: number;
  discountAmount?: number;
  gstRate: number;
  cessRate?: number;
  /** Cess charged per unit rather than as a percentage (e.g. tobacco). */
  cessPerUnit?: number;
  taxTreatment: TaxTreatment;
  accountCode?: string;
  costCentre?: string;
  /** The immediate parent line this was copied from. */
  sourceLineId?: string;
  /**
   * The line at the root of the chain. A quotation line stays the origin all
   * the way through order, delivery and invoice, so matching and fulfilment
   * work across more than one hop — `sourceLineId` alone breaks after two.
   */
  originLineId?: string;
}

/** Charges applied at document level rather than per line. */
export interface SalesCharge {
  id: string;
  label: string;
  amount: number;
  /** Charges forming part of the taxable value under section 15(2). */
  taxable: boolean;
  gstRate?: number;
  accountCode?: string;
}

export type SalesDocStatus =
  | "Draft"
  | "Pending review"
  | "Approved"
  | "Sent"
  | "Accepted"
  | "Rejected"
  | "Expired"
  | "Confirmed"
  | "Partially delivered"
  | "Delivered"
  | "Dispatched"
  | "Posted"
  | "Partially paid"
  | "Paid"
  | "Closed"
  | "Cancelled"
  | "Converted";

export interface SalesDocument {
 canonical?:boolean;adjustments?:import('../document-adjustments').DocumentAdjustments;
  id: string;
  type: SalesDocType;
  number: string;
  date: string;
  /** Due date on invoices, validity date on quotations. */
  dueDate?: string;
  partnerId: string;
  partnerName: string;
  partnerGstin?: string;
  /** Two-digit state code of the place of supply. */
  placeOfSupply: string;
  placeOfSupplyName?: string;
  supplyType: SupplyType;
  status: SalesDocStatus;
  currency: string;
  exchangeRate?: number;
  lines: SalesLine[];
  charges?: SalesCharge[];
  /** Whole-document discount applied before tax. */
  documentDiscount?: number;
  notes?: string;
  termsAndConditions?: string;
  /** Chain links — how this document came to exist. */
  quotationId?: string;
  salesOrderId?: string;
  deliveryNoteIds?: string[];
  invoiceId?: string;
  /** Credit notes carry the original invoice and the statutory reason. */
  againstInvoiceId?: string;
  creditNoteReason?: CreditNoteReason;
  /** Reverse charge — the recipient pays the tax, not the supplier. */
  rcm?: boolean;
  /** TCS under section 206C(1H) on sale of goods. */
  tcsRate?: number;
  tcsAmount?: number;
  /** TDS the customer will deduct — reduces the amount receivable, not the invoice. */
  tdsSection?: string;
  tdsRate?: number;
  /** e-Invoice fields, populated after IRP registration. */
  irn?: string;
  irnDate?: string;
  ackNumber?: string;
  signedQrCode?: string;
  eWayBillNumber?: string;
  /** Set once posted to the general ledger. */
  journalId?: string;
  postedAt?: string;
  maker?: string;
  reviewer?: string;
  createdAt: string;
  updatedAt?: string;
  /** Amount still receivable. Maintained by receipt allocation. */
  balance?: number;
}

/** Section 34 reasons — reported in GSTR-1 credit note tables. */
export type CreditNoteReason =
  | "sales-return"
  | "post-sale-discount"
  | "deficiency-in-service"
  | "correction-in-invoice"
  | "change-in-pos"
  | "finalisation-of-provisional-assessment"
  | "others";

export const CREDIT_NOTE_REASONS: Record<CreditNoteReason, string> = {
  "sales-return": "Sales return",
  "post-sale-discount": "Post-sale discount",
  "deficiency-in-service": "Deficiency in service",
  "correction-in-invoice": "Correction in invoice",
  "change-in-pos": "Change in place of supply",
  "finalisation-of-provisional-assessment": "Finalisation of provisional assessment",
  others: "Others"
};

// ------------------------------------------------------------- Numbering

export interface NumberingSeries {
  /** Pattern tokens: {PREFIX} {ENTITY} {FY} {YY} {MM} {####}. */
  pattern: string;
  prefix: string;
  nextNumber: number;
  padding: number;
  /** Sequence restarts each financial year, as rule 46(b) contemplates. */
  resetAnnually: boolean;
}

// --------------------------------------------------------------- Config

export type CreditEnforcement = "off" | "warn" | "block";

export interface SalesConfig {
  /** Quotation stage is available and may be required before an order. */
  requireQuotation: boolean;
  /**
   * When true, an invoice must originate from a confirmed sales order. This is
   * the switch you asked for: service businesses turn it off and invoice
   * directly, product businesses turn it on for order control.
   */
  requireSalesOrder: boolean;
  /** Goods must be delivered (fully or partly) before invoicing. */
  requireDeliveryNote: boolean;
  /** Invoice may cover part of an order rather than all of it. */
  allowPartialInvoicing: boolean;
  /** Delivery may exceed the ordered quantity within a tolerance. */
  allowOverDelivery: boolean;
  overDeliveryTolerancePercent: number;
  /** Invoices above the customer's credit limit warn or block. */
  creditEnforcement: CreditEnforcement;
  /** Second person must approve before an invoice posts. */
  requireInvoiceApproval: boolean;
  /** Round the invoice total to the nearest rupee, posting the difference. */
  roundOff: boolean;
  roundOffAccountCode: string;
  /** Default payment terms in days when the partner has none. */
  defaultCreditDays: number;
  /** TCS on sale of goods under section 206C(1H). */
  tcsEnabled: boolean;
  tcsRate: number;
  /** Threshold per buyer per financial year, above which TCS applies. */
  tcsThreshold: number;
  /** Supplier's own state code — decides intra versus inter-state. */
  supplierStateCode: string;
  numbering: Record<string, NumberingSeries>;
}

export const defaultSalesConfig = (supplierStateCode = "29"): SalesConfig => ({
  requireQuotation: false,
  requireSalesOrder: false,
  requireDeliveryNote: false,
  allowPartialInvoicing: true,
  allowOverDelivery: false,
  overDeliveryTolerancePercent: 0,
  creditEnforcement: "warn",
  requireInvoiceApproval: true,
  roundOff: true,
  roundOffAccountCode: "1920",
  defaultCreditDays: 30,
  tcsEnabled: false,
  tcsRate: 0.1,
  tcsThreshold: 5_000_000,
  supplierStateCode,
  numbering: {
    quotation: { pattern: "{PREFIX}/{FY}/{####}", prefix: "QT", nextNumber: 1, padding: 4, resetAnnually: true },
    "sales-order": { pattern: "{PREFIX}/{FY}/{####}", prefix: "SO", nextNumber: 1, padding: 4, resetAnnually: true },
    "delivery-note": { pattern: "{PREFIX}/{FY}/{####}", prefix: "DN", nextNumber: 1, padding: 4, resetAnnually: true },
    invoice: { pattern: "{PREFIX}/{FY}/{####}", prefix: "INV", nextNumber: 1, padding: 4, resetAnnually: true },
    "credit-note": { pattern: "{PREFIX}/{FY}/{####}", prefix: "CN", nextNumber: 1, padding: 4, resetAnnually: true },
    "debit-note": { pattern: "{PREFIX}/{FY}/{####}", prefix: "DN2", nextNumber: 1, padding: 4, resetAnnually: true },
    receipt: { pattern: "{PREFIX}/{FY}/{####}", prefix: "RCT", nextNumber: 1, padding: 4, resetAnnually: true },
    proforma: { pattern: "{PREFIX}/{FY}/{####}", prefix: "PI", nextNumber: 1, padding: 4, resetAnnually: true }
  }
});

// -------------------------------------------------------------- Receipts

export type PaymentMode = "cash" | "bank-transfer" | "cheque" | "upi" | "card" | "adjustment";

export interface ReceiptAllocation {
  invoiceId: string;
  invoiceNumber: string;
  amount: number;
}

export interface Receipt {
  id: string;
  number: string;
  date: string;
  partnerId: string;
  partnerName: string;
  amount: number;
  currency: string;
  mode: PaymentMode;
  reference?: string;
  bankAccountCode?: string;
  allocations: ReceiptAllocation[];
  /** Receipt money not yet matched to an invoice. */
  unallocated: number;
  /** TDS the customer deducted while paying — reduces cash, not revenue. */
  tdsDeducted?: number;
  tdsSection?: string;
  status: "Draft" | "Posted" | "Cancelled";
  journalId?: string;
  notes?: string;
  createdAt: string;
}

// ---------------------------------------------------------------- Audit

export interface SalesAuditEvent {
  id: string;
  documentId: string;
  documentType: SalesDocType;
  documentNumber: string;
  action: string;
  fromStatus?: SalesDocStatus;
  toStatus?: SalesDocStatus;
  user: string;
  at: string;
  /** Field-level before/after for edits. */
  changes?: Array<{ field: string; from: unknown; to: unknown }>;
  note?: string;
}

// ------------------------------------------------------- State machines

/**
 * Allowed transitions per document type. Anything not listed is rejected, so a
 * document can never reach a status the rest of the system does not expect.
 */
export const TRANSITIONS: Record<SalesDocType, Partial<Record<SalesDocStatus, SalesDocStatus[]>>> = {
  quotation: {
    Draft: ["Sent", "Cancelled"],
    Sent: ["Accepted", "Rejected", "Expired", "Cancelled"],
    Accepted: ["Converted", "Cancelled"],
    Rejected: [],
    Expired: ["Sent"],
    Converted: [],
    Cancelled: []
  },
  proforma: { Draft: ["Sent", "Cancelled"], Sent: ["Converted", "Cancelled"], Converted: [], Cancelled: [] },
  "sales-order": {
    Draft: ["Pending review", "Confirmed", "Cancelled"],
    "Pending review": ["Confirmed", "Draft", "Cancelled"],
    Confirmed: ["Partially delivered", "Delivered", "Closed", "Cancelled"],
    "Partially delivered": ["Delivered", "Closed", "Cancelled"],
    Delivered: ["Closed"],
    Closed: [],
    Cancelled: []
  },
  "delivery-note": {
    Draft: ["Dispatched", "Cancelled"],
    Dispatched: ["Delivered", "Cancelled"],
    Delivered: ["Closed"],
    Closed: [],
    Cancelled: []
  },
  invoice: {
    Draft: ["Pending review", "Posted", "Cancelled"],
    "Pending review": ["Approved", "Draft", "Cancelled"],
    Approved: ["Posted", "Draft", "Cancelled"],
    // A posted invoice is never edited or deleted. Money movement takes it to
    // partially paid or paid; a mistake is corrected by credit note.
    Posted: ["Partially paid", "Paid", "Cancelled"],
    "Partially paid": ["Paid", "Cancelled"],
    Paid: [],
    Cancelled: []
  },
  "credit-note": {
    Draft: ["Pending review", "Posted", "Cancelled"],
    "Pending review": ["Approved", "Draft", "Cancelled"],
    Approved: ["Posted", "Draft", "Cancelled"],
    Posted: [],
    Cancelled: []
  },
  "debit-note": {
    Draft: ["Pending review", "Posted", "Cancelled"],
    "Pending review": ["Approved", "Draft", "Cancelled"],
    Approved: ["Posted", "Cancelled"],
    Posted: [],
    Cancelled: []
  },
  receipt: { Draft: ["Posted", "Cancelled"], Posted: ["Cancelled"], Cancelled: [] }
};

/** Statuses after which a document is immutable. */
export const LOCKED_STATUSES: SalesDocStatus[] = ["Posted", "Partially paid", "Paid", "Cancelled", "Converted", "Closed"];

export const isLocked = (document: Pick<SalesDocument, "status">): boolean =>
  LOCKED_STATUSES.includes(document.status);

export const canTransition = (
  type: SalesDocType,
  from: SalesDocStatus,
  to: SalesDocStatus
): boolean => (TRANSITIONS[type]?.[from] ?? []).includes(to);

export const DOC_LABELS: Record<SalesDocType, string> = {
  quotation: "Quotation",
  "sales-order": "Sales order",
  "delivery-note": "Delivery note",
  invoice: "Tax invoice",
  "credit-note": "Credit note",
  "debit-note": "Debit note",
  receipt: "Receipt",
  proforma: "Proforma invoice"
};

